import React, { useState, useEffect, useCallback } from 'react';
import { S } from '../../styles';
import { API, authHeaders } from '../../utils/helpers';
import WebsiteDomains from './WebsiteDomains';

// ── Settings › Website › Pages (1 Nov) ───────────────────────────────────────
//
// The website's pages and where each stands, and the door to the page
// builder. The builder runs on the website's own address (sites/builder);
// Spinboard gives it a 12-hour pass for this person and shop
// (POST /api/website/admin/builder-session) and opens it in a new tab.

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api/website${path}`, {
    credentials: 'include', ...opts,
    headers: { ...authHeaders(!!opts.body), ...(opts.headers || {}) },
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || d.message || `Spinboard answered ${res.status}.`);
  return d;
}

const STATUS = {
  draft: ['Not published', '#8a94a6'], published: ['Published', '#2ecc71'], changed: ['Changes not published', '#e67e22'],
};

export default function WebsitePages({ T, isDark, btn, sub }) {
  const [pages, setPages] = useState(null);
  const [error, setError] = useState('');
  const [openError, setOpenError] = useState('');
  const [opening, setOpening] = useState('');

  const load = useCallback(async () => {
    try { setPages((await api('/admin/pages')).pages || []); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Coming back from the builder tab: show what changed there.
  useEffect(() => {
    const again = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', again);
    return () => document.removeEventListener('visibilitychange', again);
  }, [load]);

  // The tab is opened before asking Spinboard for the pass, so the browser
  // treats it as the click's own window and does not block it as a pop-up.
  const openBuilder = async (pageKey) => {
    setOpenError(''); setOpening(pageKey || 'all');
    const win = window.open('', '_blank');
    try {
      const d = await api('/admin/builder-session', { method: 'POST', body: JSON.stringify(pageKey ? { page: pageKey } : {}) });
      if (win) { win.opener = null; win.location.href = d.url; } else window.location.href = d.url;
    } catch (e) {
      if (win) win.close();
      setOpenError(e.message);
    }
    setOpening('');
  };

  const tile = isDark ? '#1e2a40' : '#f4f4ef';
  return (
    <>
    <WebsiteDomains T={T} isDark={isDark} btn={btn} sub={sub} onStarted={load} />
    <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
      <div style={{ ...S.settingsCardTitle, color: T.text }}>Pages</div>
      <div style={{ ...S.settingsCardDesc, color: T.textSub, marginBottom: 12 }}>
        Edit your pages in the page builder: add sections, place photos from your library, preview on a phone, save drafts,
        and publish when ready. It opens in a new tab and works for 12 hours; open it again from here after that.
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={btn(true)} disabled={!!opening} onClick={() => openBuilder(null)}>{opening === 'all' ? 'Opening…' : 'Open the page builder'}</button>
        {openError && <span style={{ fontSize: 12.5, color: '#e74c3c' }}>{openError}</span>}
      </div>

      {error && <div style={{ ...sub, color: '#e74c3c', marginTop: 12 }}>Could not load pages: {error}</div>}
      {pages && (
        <div style={{ display: 'grid', gap: 6, marginTop: 16 }}>
          {pages.map(p => {
            const [label, color] = STATUS[p.status] || [p.status, T.textSub];
            return (
              <div key={p.key} style={{ background: tile, borderRadius: 8, padding: '9px 12px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: T.text }}>{p.title || p.path}</div>
                  <div style={sub}>{p.path}</div>
                </div>
                <span style={{ fontSize: 11.5, fontWeight: 600, color }}>{label}</span>
                {p.settings?.inactive && <span style={{ fontSize: 11.5, color: T.textSub }}>Switched off</span>}
                {(p.settings?.start || p.settings?.end) && <span style={{ fontSize: 11.5, color: T.textSub }}>Scheduled</span>}
                {p.noindex && <span style={{ fontSize: 11.5, color: T.textSub }}>Hidden from Google</span>}
                <button style={btn(false)} disabled={!!opening} onClick={() => openBuilder(p.key)}>{opening === p.key ? 'Opening…' : 'Edit'}</button>
              </div>
            );
          })}
          {!pages.length && <div style={sub}>No pages yet. Start your website above.</div>}
        </div>
      )}
    </div>
    </>
  );
}
